import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

export interface Notification {
  id: string;
  type: string;
  status: 'UNREAD' | 'READ' | 'ARCHIVED';
  title: string;
  message: string;
  ticketId?: string;
  metadata?: any;
  createdAt: string;
  readAt?: string;
  ticket?: {
    id: string;
    subject: string;
    status: string;
    priority: string;
  };
}

export interface NotificationCounts {
  unread: number;
  total: number;
}

export const useNotifications = () => {
  const [pollingInterval] = useState(30000); // 30 seconds
  const queryClient = useQueryClient();

  // Get notifications
  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    error: notificationsError,
    refetch: refetchNotifications
  } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications');
      return response.data;
    },
    refetchInterval: pollingInterval,
    refetchIntervalInBackground: true
  });

  // Get notification counts
  const {
    data: countsData,
    isLoading: countsLoading,
    refetch: refetchCounts
  } = useQuery({
    queryKey: ['notification-counts'],
    queryFn: async () => {
      const response = await api.get('/notifications/counts');
      return response.data;
    },
    refetchInterval: pollingInterval,
    refetchIntervalInBackground: true
  });

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.patch(`/notifications/${notificationId}/read`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-counts'] });
    }
  });

  // Mark all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await api.patch('/notifications/read-all');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-counts'] });
    }
  });

  // Delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-counts'] });
    }
  });

  // Clear all notifications
  const clearAllMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete('/notifications/clear-all');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-counts'] });
    }
  });

  // Helper functions
  const markAsRead = useCallback((notificationId: string) => {
    markAsReadMutation.mutate(notificationId);
  }, [markAsReadMutation]);

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const deleteNotification = useCallback((notificationId: string) => {
    deleteNotificationMutation.mutate(notificationId);
  }, [deleteNotificationMutation]);

  const clearAll = useCallback(() => {
    clearAllMutation.mutate();
  }, [clearAllMutation]);

  const refreshNotifications = useCallback(() => {
    refetchNotifications();
    refetchCounts();
  }, [refetchNotifications, refetchCounts]);

  // Auto-refresh when window becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshNotifications]);

  return {
    notifications: notificationsData?.data || [],
    counts: countsData?.data || { unread: 0, total: 0 },
    isLoading: notificationsLoading || countsLoading,
    error: notificationsError,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refreshNotifications,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,
    isClearingAll: clearAllMutation.isPending
  };
};
